import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Pagination,
  CircularProgress,
  Avatar,

  FormControl,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonIcon from "@mui/icons-material/Person";
import HistoryIcon from "@mui/icons-material/History";

import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import AdminDashboardLayout from "../layout/AdminDashboardLayout";
import { getSearchQueries } from "../../../services/searchService";

export default function SearchQueriesPage() {
  const [searchQueries, setSearchQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchMode, setSearchMode] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    fetchSearchQueries(1, pageSize);
  }, []);

  const fetchSearchQueries = async (page = 1, size = pageSize) => {
    try {
      if (page === 1 && searchQueries.length === 0) {
        setLoading(true);
      } else {
        setPaginationLoading(true);
      }
      
      
      const response = await getSearchQueries(page, size);

      
      if (response && Array.isArray(response)) {
        setSearchQueries(response);
        
        // Since the API doesn't return pagination info, we'll calculate it
        const total = response.length;
        const totalPages = Math.ceil(total / size);
        setTotalPages(totalPages);
        setTotalResults(total);
        setCurrentPage(page);
      } else if (response && response.data && Array.isArray(response.data)) {
        // Fallback for if the API returns paginated response
        setSearchQueries(response.data);
        
        if (response.pagination) {
          setTotalPages(response.pagination.totalPages || 1);
          setTotalResults(response.pagination.total || 0);
          setCurrentPage(response.pagination.currentPage || page);
        }
      } else {
        console.warn("Invalid response format or no data array:", response);
        setSearchQueries([]);
      }
    } catch (err) {
      console.error("Failed to fetch search queries:", err);
      setSearchQueries([]);
      Swal.fire("Error", "Failed to fetch search queries.", "error");
    } finally {
      setLoading(false);
      setPaginationLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchInput.trim()) {
      await fetchSearchQueries(1, pageSize);
      return;
    }

    try {
      setSearchLoading(true);
      setCurrentPage(1);
      
      // For now, we'll search through the existing data
      // In a real implementation, you might want to add search parameters to the API
      const response = await getSearchQueries(1, pageSize);
      
      if (response && Array.isArray(response)) {
        const filtered = response.filter(query => {
          const searchTerm = searchInput.toLowerCase();
          
          switch (searchMode) {
            case 'searchTerm':
              return query.searchTerm?.toLowerCase().includes(searchTerm);
            case 'user':
              return (
                query.userEmail?.toLowerCase().includes(searchTerm) ||
                query.userName?.toLowerCase().includes(searchTerm)
              );
            case 'all':
            default:
              return (
                query.searchTerm?.toLowerCase().includes(searchTerm) ||
                query.userEmail?.toLowerCase().includes(searchTerm) ||
                query.userName?.toLowerCase().includes(searchTerm)
              );
          }
        });
        
        setSearchQueries(filtered);
        setTotalPages(Math.ceil(filtered.length / pageSize));
        setTotalResults(filtered.length);
      } else if (response && response.data && Array.isArray(response.data)) {
        // Fallback for paginated response
        const filtered = response.data.filter(query => {
          const searchTerm = searchInput.toLowerCase();
          
          switch (searchMode) {
            case 'searchTerm':
              return query.searchTerm?.toLowerCase().includes(searchTerm);
            case 'user':
              return (
                query.userEmail?.toLowerCase().includes(searchTerm) ||
                query.userName?.toLowerCase().includes(searchTerm)
              );
            case 'all':
            default:
              return (
                query.searchTerm?.toLowerCase().includes(searchTerm) ||
                query.userEmail?.toLowerCase().includes(searchTerm) ||
                query.userName?.toLowerCase().includes(searchTerm)
              );
          }
        });
        
        setSearchQueries(filtered);
        setTotalPages(Math.ceil(filtered.length / pageSize));
        setTotalResults(filtered.length);
      }
    } catch (err) {
      console.error("Search failed:", err);
      Swal.fire("Error", "Search failed.", "error");
    } finally {
      setSearchLoading(false);
    }
  };

  const handleClearSearch = async () => {
    setSearchInput("");
    setSearchMode("all");
    await fetchSearchQueries(1, pageSize);
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
    fetchSearchQueries(newPage, pageSize);
  };

  const handlePageSizeChange = (event) => {
    const newSize = event.target.value;
    setPageSize(newSize);
    setCurrentPage(1);
    fetchSearchQueries(1, newSize);
  };

  const handleViewUser = (userId) => {
    navigate(`/admin/userprofiledetails/${userId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (err) {
      return "Invalid Date";
    }
  };



  if (loading) {
    return (
      <AdminDashboardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <CircularProgress />
        </Box>
      </AdminDashboardLayout>
    );
  }

  return (
    <AdminDashboardLayout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ mb: 1, color: '#333333', fontWeight: 600 }}>
            Search Queries
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Monitor and analyze user search activities across the platform
          </Typography>
        </Box>

        {/* Search and Filters */}
        {/* Results Summary */}
        {/* Search Queries Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Search Term</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                                 {paginationLoading ? (
                   <TableRow>
                     <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                       <CircularProgress />
                     </TableCell>
                   </TableRow>
                 ) : searchQueries.length === 0 ? (
                   <TableRow>
                     <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                       <Typography variant="body1" color="text.secondary">
                         No search queries found
                       </Typography>
                     </TableCell>
                   </TableRow>
                 ) : (
                  searchQueries.map((query, index) => (
                    <TableRow key={query._id || index} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {query.userName || 'Unknown User'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {query.userEmail || 'No email'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200 }}>
                          {query.searchTerm || 'N/A'}
                        </Typography>
                      </TableCell>
                       
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(query.createdAt)}
                        </Typography>
                      </TableCell>
                       
                       
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Box>
    </AdminDashboardLayout>
  );
}